import React, { useEffect, useState } from 'react';
import { Prospect } from '../../shared/types';
import { automoveisStock, Vehicle } from '../../shared/data/automoveisData';
import { Car, ShieldCheck, Gauge, Zap, Filter, Search, Phone, MapPin, ChevronRight, CheckCircle2, RefreshCw } from 'lucide-react';

interface AutoDemoProps {
  prospect: Prospect;
}

export const AutoDemo: React.FC<AutoDemoProps> = ({ prospect }) => {
  const [selectedBrand, setSelectedBrand] = useState('todas');
  const [selectedCar, setSelectedCar] = useState<Vehicle | null>(null);
  const [tradeInYear, setTradeInYear] = useState('2022');
  const [tradeInKm, setTradeInKm] = useState('30000');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const filteredStock = selectedBrand === 'todas' 
    ? automoveisStock 
    : automoveisStock.filter(v => v.brand.toLowerCase() === selectedBrand.toLowerCase());

  return (
    <div className="min-h-screen bg-[#09090B] text-white font-sans selection:bg-[#DC2626] selection:text-white">
      
      {/* Header Luxury Automotive - STRICT NO SHADOWS */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-[#09090B]/90 backdrop-blur-md border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-8 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
             <div className="w-10 h-10 rounded-xl bg-[#DC2626] text-white flex items-center justify-center font-black">
                <Car className="w-5 h-5" />
             </div>
             <div>
                <h1 className="text-xl font-heading font-black text-white tracking-tight uppercase leading-none">{prospect.trade_name}</h1>
                <span className="text-[10px] text-zinc-400 font-bold tracking-widest uppercase">Supercars & Blindados</span>
             </div>
          </div>

          <nav className="hidden md:flex items-center gap-8 text-xs font-bold uppercase tracking-wider text-zinc-400">
             <a href="#estoque" className="hover:text-white transition-colors">Estoque Completo</a>
             <a href="#simulador" className="hover:text-white transition-colors">Avaliar Meu Usado</a>
             <a href="#laudo" className="hover:text-white transition-colors">Garantia & Laudo</a>
          </nav>

          <button className="bg-[#DC2626] text-white px-5 py-2.5 rounded-xl font-bold text-xs uppercase tracking-wider hover:bg-red-700 transition-colors flex items-center gap-2 border border-red-500">
             <Phone className="w-4 h-4" />
             <span>Falar com Concierge</span>
          </button>
        </div>
      </header>

      {/* Hero Impactante Esportivo - STRICT NO SHADOWS */}
      <section className="pt-36 pb-20 px-8 relative overflow-hidden border-b border-zinc-800 bg-[#09090B]">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
           <div className="lg:col-span-7 space-y-6">
              <span className="px-3 py-1 rounded-xl bg-zinc-800 border border-zinc-700 text-red-500 text-xs font-black uppercase tracking-widest inline-block">
                 Procedência & Laudo Cautelar 100%
              </span>
              <h2 className="text-5xl lg:text-7xl font-heading font-black text-white uppercase tracking-tight leading-[1.05]">
                 Performance & <br />
                 <span className="text-transparent bg-clip-text bg-gradient-to-r from-red-500 to-zinc-400">
                    Sofisticação.
                 </span>
              </h2>
              <p className="text-base text-zinc-400 font-light max-w-xl leading-relaxed">
                 Showroom exclusivo com os superesportivos e blindados mais desejados do mercado. Entrega técnica em todo o Brasil.
              </p>

              {/* Filtro Rápido no Hero - STRICT NO SHADOWS */}
              <div className="bg-zinc-900 border border-zinc-800 p-4 rounded-xl max-w-xl mt-6 flex flex-wrap gap-3">
                 <div className="flex-1 min-w-[140px]">
                    <span className="text-[10px] text-zinc-500 font-bold uppercase block mb-1">Marca</span>
                    <select 
                       onChange={(e) => setSelectedBrand(e.target.value)} 
                       className="w-full bg-zinc-800 border border-zinc-700 rounded-lg p-2 text-xs font-bold text-white outline-none"
                    >
                       <option value="todas">Todas as Marcas</option>
                       <option value="porsche">Porsche</option>
                       <option value="bmw">BMW</option>
                       <option value="audi">Audi</option>
                       <option value="mercedes-amg">Mercedes-AMG</option>
                    </select>
                 </div>
                 <button className="bg-[#DC2626] text-white px-6 py-2 rounded-lg font-bold text-xs uppercase tracking-wider self-end hover:bg-red-700 transition-colors">
                    Filtrar ({filteredStock.length})
                 </button>
              </div>
           </div>

           <div className="lg:col-span-5">
              <div className="relative rounded-2xl overflow-hidden border border-zinc-800 bg-zinc-900 p-2">
                 <div className="aspect-[4/3] rounded-xl overflow-hidden">
                    <img 
                      src={automoveisStock[0].image} 
                      alt="Supercar" 
                      className="w-full h-full object-cover"
                    />
                 </div>
                 <div className="p-4 flex items-center justify-between border-t border-zinc-800 mt-2">
                    <div>
                       <h4 className="font-bold text-sm text-white">{automoveisStock[0].brand} {automoveisStock[0].model}</h4>
                       <p className="text-xs text-zinc-400">{automoveisStock[0].power} • {automoveisStock[0].year}</p>
                    </div>
                    <span className="text-lg font-black text-red-500">R$ {(automoveisStock[0].price / 1000000).toFixed(2)}M</span>
                 </div>
              </div>
           </div>
        </div>
      </section>

      {/* Grid do Estoque - STRICT NO SHADOWS */}
      <section id="estoque" className="py-24 px-8 max-w-7xl mx-auto">
         <div className="flex justify-between items-end mb-12 pb-4 border-b border-zinc-800">
            <div>
               <span className="text-xs font-bold text-red-500 uppercase tracking-widest">Veículos Disponíveis</span>
               <h3 className="text-3xl font-heading font-black uppercase text-white mt-1">Estoque em Destaque</h3>
            </div>
            <span className="text-xs text-zinc-400 font-bold">{filteredStock.length} veículos encontrados</span>
         </div>

         <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {filteredStock.map((car) => (
               <div key={car.id} className="bg-zinc-900 rounded-xl border border-zinc-800 overflow-hidden hover:border-zinc-600 transition-colors group flex flex-col justify-between">
                  <div>
                     <div className="relative aspect-[16/10] overflow-hidden">
                        <img src={car.image} alt={car.model} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                        {car.badge && (
                           <div className="absolute top-3 left-3 bg-zinc-950/90 text-white text-[10px] font-black uppercase tracking-widest px-3 py-1 rounded-md border border-zinc-700">
                              {car.badge}
                           </div>
                        )}
                     </div>
                     <div className="p-6">
                        <span className="text-xs font-bold text-red-500 uppercase tracking-wider">{car.brand}</span>
                        <h4 className="font-heading font-black text-xl text-white mb-1">{car.model}</h4>
                        <p className="text-xs text-zinc-400 font-light mb-4">{car.version}</p>

                        <div className="grid grid-cols-2 gap-2 text-xs bg-zinc-950 p-3 rounded-lg border border-zinc-800/80 mb-4">
                           <div className="flex items-center gap-1.5 text-zinc-300">
                              <Gauge className="w-3.5 h-3.5 text-red-500" /> {car.km}
                           </div>
                           <div className="flex items-center gap-1.5 text-zinc-300">
                              <Zap className="w-3.5 h-3.5 text-red-500" /> {car.power}
                           </div>
                        </div>
                     </div>
                  </div>

                  <div className="px-6 pb-6 pt-0 flex items-center justify-between border-t border-zinc-800/50 mt-auto pt-4">
                     <span className="text-xl font-black text-white">R$ {(car.price / 1000).toLocaleString('pt-BR')}.000</span>
                     <button className="bg-zinc-800 hover:bg-red-600 text-white px-4 py-2 rounded-lg text-xs font-bold uppercase transition-colors border border-zinc-700">
                        Propor Troca
                     </button>
                  </div>
               </div>
            ))}
         </div>
      </section>

      {/* Simulador de Troca do Usado - STRICT NO SHADOWS */}
      <section id="simulador" className="py-20 px-8 bg-zinc-900 border-t border-zinc-800">
         <div className="max-w-4xl mx-auto bg-zinc-950 p-10 rounded-2xl border border-zinc-800">
            <div className="text-center mb-8">
               <span className="text-xs font-bold uppercase tracking-widest text-red-500 flex items-center justify-center gap-2">
                  <RefreshCw className="w-4 h-4" /> Avaliação Transparente
               </span>
               <h3 className="text-3xl font-heading font-black text-white uppercase mt-2">Avalie Seu Usado na Troca</h3>
               <p className="text-xs text-zinc-400 font-light mt-1">Aceitamos veículos de alto padrão com troco na troca e quitação de financiamento.</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
               <input type="text" placeholder="Marca e Modelo do Seu Carro (Ex: BMW X5)" className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-xs text-white placeholder:text-zinc-500 outline-none" />
               <input type="text" placeholder="Ano de Fabricação" defaultValue={tradeInYear} onChange={e => setTradeInYear(e.target.value)} className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 text-xs text-white outline-none" />
            </div>

            <button className="w-full bg-red-600 text-white font-black text-xs uppercase tracking-widest py-4 rounded-xl hover:bg-red-700 transition-colors border border-red-500">
               Solicitar Cotação de Avaliação via Concierge
            </button>
         </div>
      </section>

      {/* Footer Luxury Auto */}
      <footer className="bg-[#09090B] text-zinc-400 py-12 px-8 border-t border-zinc-800 text-xs">
         <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="flex items-center gap-3">
               <Car className="w-5 h-5 text-red-500" />
               <span className="font-bold text-white text-sm">{prospect.trade_name}</span>
            </div>
            <span>Av. Europa, 780 - Jardins, São Paulo - SP</span>
            <span>&copy; {new Date().getFullYear()} YouMachine Automotive Engine.</span>
         </div>
      </footer>

    </div>
  );
};
